import { Avatar, Box, Stack, styled } from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

const BubbleContainer = styled(Stack, {
  shouldForwardProp: (p) => p !== "isAi",
})<{ isAi: boolean }>(({ theme, isAi }) => ({
  flexDirection: isAi ? "row" : "row-reverse",
  alignItems: "flex-end",
  alignSelf: isAi ? "flex-start" : "flex-end",
  gap: theme.spacing(1.25),
  maxWidth: "92%",
  margin: theme.spacing(1.25, 0),
}));

const Bubble = styled(Box, {
  shouldForwardProp: (p) => p !== "isAi",
})<{ isAi: boolean }>(({ theme, isAi }) => ({
  background: isAi
    ? theme.palette.background.paper
    : theme.palette.mode === "dark"
      ? "rgba(84, 189, 193, 0.18)"
      : theme.palette.primary.light,
  color: theme.palette.text.primary,
  border: isAi ? `1px solid ${theme.palette.divider}` : "none",
  borderRadius: theme.spacing(4),
  padding: theme.spacing(1.5, 2),
  boxShadow: isAi ? "0 1px 2px rgba(0,0,0,0.04)" : "none",
  minWidth: 0,
  wordBreak: "break-word",
}));

interface ChatBubbleProps {
  isAi: boolean;
  children: React.ReactNode;
  /**
   * Optional small footer slot rendered below the bubble content,
   * right-aligned. Used for the per-message "copy" action so the
   * parent does not have to break out of the bubble's flex layout.
   */
  footer?: React.ReactNode;
}

const ChatBubble: React.FC<ChatBubbleProps> = ({ isAi, children, footer }) => {
  return (
    <BubbleContainer isAi={isAi}>
      <Avatar
        sx={{
          width: 28,
          height: 28,
          bgcolor: isAi ? "primary.50" : "secondary.main",
          color: isAi ? "primary.dark" : "secondary.contrastText",
          fontSize: 14,
        }}
      >
        {isAi ? (
          <SmartToyOutlinedIcon sx={{ fontSize: 16 }} />
        ) : (
          <PersonOutlineIcon sx={{ fontSize: 16 }} />
        )}
      </Avatar>
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, maxWidth: "100%" }}>
        <Bubble isAi={isAi}>{children}</Bubble>
        {footer ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: isAi ? "flex-start" : "flex-end",
              mt: 0.5,
              px: 0.5,
            }}
          >
            {footer}
          </Box>
        ) : null}
      </Box>
    </BubbleContainer>
  );
};

export default ChatBubble;
